import React, { useState } from 'react';
import CardGridItem from './CardGridItem';
import CardDetailModal from './CardDetailModal';
import CardFilters from './CardFilters';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import { SORT_FIELDS } from '../../utils/constants';

export default function CardGrid({
  cards, total, pages, page, setPage,
  sortBy, sortOrder, toggleSort,
  filters, updateFilters,
  loading, error, updateCard, deleteCard,
}) {
  const [selectedCard, setSelectedCard] = useState(null);

  const handleCardClick = (card) => {
    setSelectedCard(card);
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
  };

  // Wrap updateCard to also update selectedCard in-place if it's the same card
  const handleUpdateCard = async (cardId, data) => {
    await updateCard(cardId, data);
    // Close modal after update so refreshed data is shown when reopened
    if (selectedCard?.id === cardId) {
      setSelectedCard(null);
    }
  };

  const handleDeleteCard = async (cardId) => {
    await deleteCard(cardId);
  };

  // Sort handler for dropdown — set field and reset order
  const handleSortChange = (field) => {
    // toggleSort handles both field change and order toggle
    if (field !== sortBy) {
      // Changing field — toggleSort will set it with default order
      toggleSort(field);
    }
  };

  const handleOrderToggle = () => {
    // Toggle order on same field
    toggleSort(sortBy);
  };

  return (
    <div className="card-grid-container">
      <CardFilters filters={filters} onFilterChange={updateFilters} />

      {/* Sort bar */}
      <div className="card-grid-toolbar">
        <div className="card-grid-count">
          {total} card{total !== 1 ? 's' : ''}
        </div>
        <div className="card-grid-sort">
          <label>Sort by</label>
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            {SORT_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
          <button
            className="btn btn-sm card-grid-sort-order"
            onClick={handleOrderToggle}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? '\u2191' : '\u2193'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <LoadingSpinner text="Loading cards..." />
      ) : cards.length === 0 ? (
        <div className="empty-state">
          <p>No cards found. Try adjusting your filters or run a scrape to populate the database.</p>
        </div>
      ) : (
        <>
          <div className="card-grid">
            {cards.map((card) => (
              <CardGridItem
                key={card.id}
                card={card}
                onClick={handleCardClick}
              />
            ))}
          </div>
          <Pagination
            page={page}
            pages={pages}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Detail modal */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={handleCloseModal}
          updateCard={handleUpdateCard}
          deleteCard={handleDeleteCard}
        />
      )}
    </div>
  );
}
